import { useMemo } from 'react';
import { FaSearch, FaUserCircle } from 'react-icons/fa';
import NotificationBell from '../NotificationBell';
import { SearchSuggestionList } from '../SearchSuggestionList';
import { HeaderProps } from './types';
import { useHeader } from './useHeader';
import styles from './Header.module.css';
import DropdownMenu from './DropdownMenu';
import HeaderOverlay from './HeaderOverlay';
import { toRelativePath } from '../../utils/videoImagePath';

const Header = ({ currentUser, className }: HeaderProps) => {
  const {
    state,
    suggestions,
    isInputFocused,
    selectedSuggestionIndex,
    menuRef,
    handleLogout,
    handleAccount,
    handleLogoClick,
    handleNotificationToggle,
    toggleMenu,
    handleSearchSubmit,
    handleBlur,
    handleKeyDown,
    handleSuggestionKeyDown,
    handleSuggestionClick,
    handleFocus,
    handleAdminNotifications,
    handleSearchQueryChange,
  } = useHeader(currentUser);

  // アイコンのフルURL（キャッシュバスターを追加して即時反映を確保）
  // icon_pathが変更されたときに確実に新しいURLが生成されるようにする
  // currentUserオブジェクト全体を依存配列に含めて、確実に再計算されるようにする
  const iconUrl = useMemo(() => {
    if (!currentUser?.icon_path || currentUser.icon_path.trim() === '') return null;
    // icon_pathの値自体をキャッシュバスターとして使用
    // これにより、icon_pathが変更されたときに確実に新しいURLが生成される
    // パスに含まれる特殊文字をエンコードして使用
    const cacheBuster = encodeURIComponent(currentUser.icon_path);
    // 先頭スラッシュを正規化（'uploads/...' と '/uploads/...' の両パターンに対応）
    const normalizedPath = toRelativePath(currentUser.icon_path);
    return `${normalizedPath}?v=${cacheBuster}`;
  }, [currentUser?.icon_path, currentUser]);

  // SP表示の場合はヘッダーを表示しない
  if (state.isMobile) {
    return (
      <>
        <div className={`${styles.mobileSearchContainer} ${!state.searchBarVisible ? styles.hidden : ''} ${state.searchBarCompact ? styles.compact : ''}`}>
          <form onSubmit={handleSearchSubmit} className={styles.mobileSearchForm}>
            <div className={styles.mobileSearch} style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="検索..."
                className={styles.searchInput}
                value={state.searchQuery}
                onChange={handleSearchQueryChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                data-testid="header__search-input-mobile"
              />
              <SearchSuggestionList
                suggestions={suggestions}
                isVisible={isInputFocused}
                selectedIndex={selectedSuggestionIndex}
                onSuggestionClick={handleSuggestionClick}
                onSuggestionKeyDown={handleSuggestionKeyDown}
                testIdPrefix="header__suggestion"
                className={styles.suggestionsContainer}
                itemClassName={styles.suggestionItem}
                selectedItemClassName={styles.suggestionItemSelected}
                renderSuggestionContent={(suggestion, index) => (
                  <>
                    <span className={styles.suggestionType}>{suggestion.type}</span>
                    <span className={styles.suggestionValue}>{suggestion.display_text || suggestion.value}</span>
                  </>
                )}
              />
            </div>
          </form>
        </div>
      </>
    );
  }

  return (
    <>
      <header className={`${styles.header} ${className || ''}`}>
        <div className={styles.headerLeft}>
          <img
            src="/asset/logo.svg"
            alt="Logo"
            className={styles.logoImage}
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
            data-testid="header__logo"
          />
        </div>

        <div className={styles.headerCenter}>
          <form onSubmit={handleSearchSubmit} className={styles.headerSearchForm}>
            <div className={styles.headerSearchContainer} style={{ position: 'relative' }}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="キーワードを入力してください"
                className={styles.searchInput}
                value={state.searchQuery}
                onChange={handleSearchQueryChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                data-testid="header__search-input"
              />
              <button type="submit" className={styles.searchButton}>
                検索
              </button>
              {/* isInputFocused が true で、かつ候補がある場合のみ表示 */}
              <SearchSuggestionList
                suggestions={suggestions}
                isVisible={isInputFocused}
                selectedIndex={selectedSuggestionIndex}
                onSuggestionClick={handleSuggestionClick}
                onSuggestionKeyDown={handleSuggestionKeyDown}
                testIdPrefix="header__suggestion"
                className={styles.suggestionsContainer}
                itemClassName={styles.suggestionItem}
                selectedItemClassName={styles.suggestionItemSelected}
                renderSuggestionContent={(suggestion, index) => (
                  <>
                    <span className={styles.suggestionType}>{suggestion.type}</span>
                    <span className={styles.suggestionValue}>{suggestion.display_text || suggestion.value}</span>
                  </>
                )}
              />
            </div>
          </form>
        </div>

        <div className={styles.headerRight} ref={menuRef}>
          
          {/* 通知ベルアイコンを追加 */}
          <div className={`${styles.notificationBellContainer} ${state.hasUnreadNotifications ? styles.hasUnreadParent : ''}`}>
            <NotificationBell 
              onToggle={handleNotificationToggle}
            />
          </div>
          <div
            className={styles.userMenuTrigger}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleMenu();
            }}
          >
            {iconUrl ? (
              <img
                src={iconUrl}
                alt="User Icon"
                className={styles.profileIcon}
                data-testid="header__user-icon"
              />
            ) : (
              <FaUserCircle
                className={styles.profileIcon}
                data-testid="header__user-icon-fallback"
              />
            )}
            <span className={styles.menuLabel}>メニュー</span>
            <svg className={styles.menuChevron} width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path d="M1 1L5 5L9 1" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </header>
      
      {/* ドロップダウンメニューとオーバーレイをPortalを使って表示 */}
      {(() => {
        if (state.menuOpen) {
          return (
            <>
              <HeaderOverlay onClick={() => {
                toggleMenu();
              }} />
              <DropdownMenu
                currentUser={currentUser}
                iconUrl={iconUrl}
                handleAccount={handleAccount}
                handleLogout={handleLogout}
                handleAdminNotifications={handleAdminNotifications}
                menuRef={menuRef}
              />
            </>
          );
        }
        return null;
      })()}
    </>
  );
};

export default Header; 
